import { Fragment, type ReactNode } from 'react';
import styles from './Preview.module.css';

const INLINE_TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function safeUrl(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function inlineMarkdown(text: string): ReactNode[] {
  return text.split(INLINE_TOKEN).map((token, index) => {
    if (token.startsWith('**') && token.endsWith('**')) {
      return <strong key={`${index}-${token}`}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith('`') && token.endsWith('`')) {
      return <code key={`${index}-${token}`}>{token.slice(1, -1)}</code>;
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
    if (link) {
      const label = link[1] ?? '';
      const href = safeUrl(link[2] ?? '');
      return href ? (
        <a key={`${index}-${token}`} href={href} target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <Fragment key={`${index}-${token}`}>{label}</Fragment>
      );
    }
    return <Fragment key={`${index}-${token}`}>{token}</Fragment>;
  });
}

export function MarkdownPreview({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks = lines.reduce<{ inFence: boolean; nodes: ReactNode[] }>(
    (state, line, index) => {
      if (line.trim().startsWith('```')) {
        return { ...state, inFence: !state.inFence };
      }
      if (state.inFence) {
        return { ...state, nodes: [...state.nodes, <pre key={index}>{line || ' '}</pre>] };
      }

      let node: ReactNode;
      if (line.startsWith('### ')) node = <h3 key={index}>{inlineMarkdown(line.slice(4))}</h3>;
      else if (line.startsWith('## ')) node = <h2 key={index}>{inlineMarkdown(line.slice(3))}</h2>;
      else if (line.startsWith('# ')) node = <h1 key={index}>{inlineMarkdown(line.slice(2))}</h1>;
      else if (/^[-*] /.test(line)) {
        node = (
          <div className={styles.listItem} key={index}>
            • {inlineMarkdown(line.slice(2))}
          </div>
        );
      } else if (line.startsWith('> ')) {
        node = <blockquote key={index}>{inlineMarkdown(line.slice(2))}</blockquote>;
      } else if (!line.trim()) {
        node = <div className={styles.markdownGap} aria-hidden="true" key={index} />;
      } else {
        node = <p key={index}>{inlineMarkdown(line)}</p>;
      }

      return { ...state, nodes: [...state.nodes, node] };
    },
    { inFence: false, nodes: [] },
  ).nodes;

  return <div className={styles.markdown}>{blocks}</div>;
}
